import React, { useEffect, useRef, useState } from 'react';
import { adminGroupBuyApi, uploadApi, categoryApi } from '../../api';
import { GroupBuy, GroupBuyStatus, GoodsType } from '../../types';
import { toImageUrl } from '../../utils/image';

interface GbDescImage { url: string; displayOrder: number; }

const STATUS_TRANSITIONS: Record<GroupBuyStatus, GroupBuyStatus[]> = {
  COLLECTING: ['CLOSED'],
  CLOSED: ['PURCHASING', 'FAILED'],
  PURCHASING: ['SHIPPING'],
  SHIPPING: ['DONE'],
  DONE: [],
  FAILED: [],
};

const STATUS_COLOR: Record<GroupBuyStatus, string> = {
  COLLECTING: '#b39ddb', CLOSED: '#888', PURCHASING: '#fb8c00',
  SHIPPING: '#1976d2', DONE: '#4caf50', FAILED: '#e53935',
};

const STATUS_LABEL: Record<GroupBuyStatus, string> = {
  COLLECTING: '모집중', CLOSED: '마감', PURCHASING: '구매중',
  SHIPPING: '배송중', DONE: '완료', FAILED: '무산',
};

const GOODS_TYPES: GoodsType[] = ['KEYRING', 'ACRYLIC', 'STICKER', 'POSTER', 'BADGE', 'TAPESTRY', 'OTHER'];

const GOODS_TYPE_LABEL: Record<GoodsType, string> = {
  KEYRING: '키링', ACRYLIC: '아크릴', STICKER: '스티커', POSTER: '포스터',
  BADGE: '뱃지', TAPESTRY: '태피스트리', OTHER: '기타',
};

interface GbVariantForm { name: string; imageUrl: string; available: boolean; file: File | null; }

const EMPTY_FORM = {
  code: '', title: '', description: '', webtoonTitle: '', artist: '',
  goodsType: 'KEYRING' as GoodsType,
  categoryId: '',
  startAt: '', deadline: '', stock: 100, priceUsd: '', estimatedShippingWeeks: '2-4 weeks',
  maxPerPerson: '', thumbnailUrl: '', weightGrams: 100,
  variants: [] as GbVariantForm[],
};

const AdminGroupBuys: React.FC = () => {
  const [items, setItems] = useState<GroupBuy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<GroupBuy | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState('');

  useEffect(() => {
    categoryApi.getAll().then(r => {
      const flat: { id: number; name: string }[] = [];
      const walk = (cats: any[]) => cats.forEach(c => { flat.push({ id: c.id, name: c.name }); if (c.children) walk(c.children); });
      walk(r.data.data as any[]);
      setCategories(flat);
    }).catch(() => {});
  }, []);

  // 썸네일
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [thumbPreview, setThumbPreview] = useState('');
  const thumbInputRef = useRef<HTMLInputElement>(null);
  // 설명 이미지 (기존 url + 신규 file 병렬 배열)
  const [descImages, setDescImages] = useState<GbDescImage[]>([]);
  const [descFiles, setDescFiles] = useState<(File | null)[]>([]);
  const descInputRef = useRef<HTMLInputElement>(null);

  const resetImageState = () => {
    setThumbFile(null); setThumbPreview(''); setDescImages([]); setDescFiles([]);
  };

  const onDescFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setDescImages(prev => [...prev, ...files.map((_, i) => ({ url: '', displayOrder: prev.length + i + 1 }))]);
    setDescFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };
  const removeDescImage = (idx: number) => {
    setDescImages(prev => prev.filter((_, i) => i !== idx).map((img, i) => ({ ...img, displayOrder: i + 1 })));
    setDescFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const load = () => {
    setLoading(true);
    adminGroupBuyApi.getList(undefined, 0).then(r => setItems(r.data.data.content)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM, variants: [] }); resetImageState(); setShowForm(true); };
  const openEdit = (gb: GroupBuy) => {
    setEditing(gb);
    setForm({
      code: gb.code, title: gb.title, categoryId: gb.categoryId ? String(gb.categoryId) : '', description: gb.description ?? '',
      webtoonTitle: gb.webtoonTitle ?? '', artist: gb.artist ?? '',
      goodsType: gb.goodsType,
      startAt: gb.startAt ? gb.startAt.slice(0, 16) : '',
      deadline: gb.deadline.slice(0, 16),
      stock: gb.stock, priceUsd: String(gb.priceUsd),
      estimatedShippingWeeks: gb.estimatedShippingWeeks ?? '2-4 weeks',
      maxPerPerson: gb.maxPerPerson ? String(gb.maxPerPerson) : '',
      thumbnailUrl: gb.thumbnailUrl ?? '',
      weightGrams: gb.weightGrams,
      variants: [...(gb.variants ?? [])]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(v => ({ name: v.name, imageUrl: v.imageUrl ?? '', available: v.available, file: null })),
    });
    setThumbFile(null);
    setThumbPreview(gb.thumbnailUrl ? toImageUrl(gb.thumbnailUrl) : '');
    const imgs = [...(gb.images ?? [])].sort((a, b) => a.displayOrder - b.displayOrder)
      .map((im, i) => ({ url: im.url, displayOrder: i + 1 }));
    setDescImages(imgs);
    setDescFiles(imgs.map(() => null));
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.title || !form.deadline || !form.priceUsd) {
      alert('코드, 제목, 마감일, 가격은 필수입니다.');
      return;
    }
    setSaving(true);
    try {
      // 썸네일 업로드
      let thumbnailUrl: string | null = thumbPreview && !thumbFile ? (editing?.thumbnailUrl ?? null) : null;
      if (thumbFile) thumbnailUrl = (await uploadApi.image(thumbFile)).data.data;

      // 설명 이미지 업로드(신규 file) 또는 기존 url 유지
      const images: { url: string; displayOrder: number }[] = [];
      for (let i = 0; i < descImages.length; i++) {
        const file = descFiles[i];
        if (file) images.push({ url: (await uploadApi.image(file)).data.data, displayOrder: i + 1 });
        else if (descImages[i].url) images.push({ url: descImages[i].url, displayOrder: i + 1 });
      }

      // 옵션 이미지 업로드
      const variants: { name: string; imageUrl: string | null; displayOrder: number; available: boolean }[] = [];
      for (let i = 0; i < form.variants.length; i++) {
        const v = form.variants[i];
        if (!v.name.trim()) continue;
        let imageUrl: string | null = v.imageUrl || null;
        if (v.file) imageUrl = (await uploadApi.image(v.file)).data.data;
        variants.push({ name: v.name.trim(), imageUrl, displayOrder: i + 1, available: v.available });
      }

      const payload = {
        ...form,
        thumbnailUrl,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        stock: Number(form.stock),
        priceUsd: Number(form.priceUsd),
        maxPerPerson: form.maxPerPerson ? Number(form.maxPerPerson) : null,
        weightGrams: Number(form.weightGrams),
        startAt: form.startAt ? form.startAt + ':00' : null,
        deadline: form.deadline + ':00',
        images,
        variants,
      };
      if (editing) {
        await adminGroupBuyApi.update(editing.id, payload);
      } else {
        await adminGroupBuyApi.create(payload);
      }
      setShowForm(false);
      resetImageState();
      load();
    } catch (e: any) {
      alert(e.response?.data?.message ?? '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 한정판매를 삭제할까요?')) return;
    await adminGroupBuyApi.delete(id);
    load();
  };

  const handleTransition = async (id: number, status: GroupBuyStatus) => {
    if (!window.confirm(`'${STATUS_LABEL[status]}' 상태로 변경할까요?${status === 'FAILED' ? ' 참여자 전원에게 환불이 진행됩니다!' : ''}`)) return;
    try {
      await adminGroupBuyApi.updateStatus(id, status);
      load();
    } catch (e: any) {
      alert(e.response?.data?.message ?? '상태 변경에 실패했습니다.');
    }
  };

  const addVariant = () => setForm(p => ({ ...p, variants: [...p.variants, { name: '', imageUrl: '', available: true, file: null }] }));
  const removeVariant = (i: number) => setForm(p => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }));
  const setVariant = (i: number, key: keyof GbVariantForm, val: any) =>
    setForm(p => { const v = [...p.variants]; v[i] = { ...v[i], [key]: val }; return { ...p, variants: v }; });
  const onVariantImage = (i: number, file: File | null) =>
    setForm(p => { const v = [...p.variants]; v[i] = { ...v[i], file, imageUrl: file ? '' : v[i].imageUrl }; return { ...p, variants: v }; });

  const f = (key: string) => ({
    value: (form as any)[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [key]: e.target.value })),
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>한정판매 관리</h2>
        <button style={styles.createBtn} onClick={openCreate}>+ 한정판매 추가</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>카테고리</label>
        <select style={{ ...styles.formInput, width: 'auto', minWidth: 160 }}
          value={filterCategoryId} onChange={e => setFilterCategoryId(e.target.value)}>
          <option value="">전체</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? <p>불러오는 중...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.th}>
                {['썸네일', '제목', '카테고리', '상태', '재고', '판매', '가격', '마감일', '관리'].map(h => (
                  <th key={h} style={styles.thCell}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items
                .filter(gb => !filterCategoryId || String(gb.categoryId) === filterCategoryId)
                .map(gb => (
                <tr key={gb.id} style={styles.tr}>
                  <td style={styles.td}>
                    <img src={toImageUrl(gb.thumbnailUrl ?? '')} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                  </td>
                  <td style={styles.td}>
                    <p style={{ fontWeight: 600, fontSize: 13 }}>{gb.title}</p>
                    {gb.webtoonTitle && <p style={{ fontSize: 11, color: '#aaa' }}>{gb.webtoonTitle}</p>}
                  </td>
                  <td style={styles.td}>{gb.categoryName ?? '-'}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, background: STATUS_COLOR[gb.status] }}>{STATUS_LABEL[gb.status]}</span>
                  </td>
                  <td style={styles.td}>{gb.stock}</td>
                  <td style={styles.td}>{gb.currentQuantity}</td>
                  <td style={styles.td}>${Number(gb.priceUsd).toFixed(2)}</td>
                  <td style={styles.td}>{new Date(gb.deadline).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={styles.editBtn} onClick={() => openEdit(gb)}>수정</button>
                      {STATUS_TRANSITIONS[gb.status].map(s => (
                        <button key={s}
                          style={{ ...styles.statusBtn, background: STATUS_COLOR[s] }}
                          onClick={() => handleTransition(gb.id, s)}
                        >→ {STATUS_LABEL[s]}</button>
                      ))}
                      {gb.status === 'COLLECTING' && (
                        <button style={styles.deleteBtn} onClick={() => handleDelete(gb.id)}>삭제</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
              {editing ? '한정판매 수정' : '한정판매 등록'}
            </h3>
            <div style={styles.formGrid}>
              {[
                { key: 'code', label: '코드 *', type: 'text', disabled: !!editing },
                { key: 'title', label: '제목 *', type: 'text' },
                { key: 'webtoonTitle', label: '웹툰 제목', type: 'text' },
                { key: 'artist', label: '작가', type: 'text' },
                { key: 'startAt', label: '모집 시작일 (비우면 즉시 시작)', type: 'datetime-local' },
                { key: 'deadline', label: '마감일 *', type: 'datetime-local' },
                { key: 'stock', label: '재고 *', type: 'number' },
                { key: 'priceUsd', label: '가격(USD) *', type: 'number', step: '0.01' },
                { key: 'estimatedShippingWeeks', label: '예상 배송기간', type: 'text' },
                { key: 'maxPerPerson', label: '1인 최대수량', type: 'number' },
                { key: 'weightGrams', label: '무게(g)', type: 'number' },
              ].map(({ key, label, type, disabled, step }: any) => (
                <div key={key} style={styles.formField}>
                  <label style={styles.formLabel}>{label}</label>
                  <input type={type} step={step} style={styles.formInput} {...f(key)} disabled={disabled} />
                </div>
              ))}
              <div style={styles.formField}>
                <label style={styles.formLabel}>굿즈 종류</label>
                <select style={styles.formInput} value={form.goodsType}
                  onChange={e => setForm(p => ({ ...p, goodsType: e.target.value as GoodsType }))}>
                  {GOODS_TYPES.map(t => <option key={t} value={t}>{GOODS_TYPE_LABEL[t]}</option>)}
                </select>
              </div>
              <div style={styles.formField}>
                <label style={styles.formLabel}>카테고리</label>
                <select style={styles.formInput} value={form.categoryId}
                  onChange={e => setForm(p => ({ ...p, categoryId: e.target.value }))}>
                  <option value="">(없음)</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>썸네일 이미지</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {thumbPreview && <img src={thumbPreview} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e8e8e8' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                <button type="button" style={styles.editBtn} onClick={() => thumbInputRef.current?.click()}>{thumbPreview ? '이미지 변경' : '이미지 선택'}</button>
                {thumbPreview && <button type="button" style={styles.deleteBtn} onClick={() => { setThumbFile(null); setThumbPreview(''); }}>제거</button>}
                <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { const file = e.target.files?.[0]; if (file) { setThumbFile(file); setThumbPreview(URL.createObjectURL(file)); } }} />
              </div>
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>설명</label>
              <textarea rows={4} style={{ ...styles.formInput, resize: 'vertical' }} {...f('description')} />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>상품 설명 이미지 (순서대로)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {descImages.map((img, idx) => {
                  const src = descFiles[idx] ? URL.createObjectURL(descFiles[idx]!) : (img.url ? toImageUrl(img.url) : '');
                  return (
                    <div key={idx} style={{ position: 'relative' }}>
                      {src && <img src={src} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                      <button type="button" onClick={() => removeDescImage(idx)}
                        style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#e53935', color: '#fff', fontSize: 12, cursor: 'pointer' }}>✕</button>
                    </div>
                  );
                })}
              </div>
              <button type="button" style={{ ...styles.editBtn, marginTop: 8 }} onClick={() => descInputRef.current?.click()}>+ 이미지 추가</button>
              <input ref={descInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onDescFilesChange} />
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={styles.formLabel}>디자인 옵션 (선택) — 디자인만 다른 경우. 가격·무게 공통, 재고 없음(MOQ 모집)</label>
              {form.variants.map((v, i) => {
                const vsrc = v.file ? URL.createObjectURL(v.file) : (v.imageUrl ? toImageUrl(v.imageUrl) : '');
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                    {vsrc
                      ? <img src={vsrc} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : <div style={{ width: 40, height: 40, background: '#f5f5f5', borderRadius: 6, flexShrink: 0 }} />}
                    <input style={{ ...styles.formInput, flex: 1 }} placeholder="디자인명 (예: Red)" value={v.name}
                      onChange={e => setVariant(i, 'name', e.target.value)} />
                    <label style={{ ...styles.editBtn, whiteSpace: 'nowrap' }}>
                      {v.file || v.imageUrl ? '이미지 변경' : '이미지'}
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => onVariantImage(i, e.target.files?.[0] ?? null)} />
                    </label>
                    <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={v.available} onChange={e => setVariant(i, 'available', e.target.checked)} />판매
                    </label>
                    <button type="button" style={styles.deleteBtn} onClick={() => removeVariant(i)}>✕</button>
                  </div>
                );
              })}
              <button type="button" style={{ ...styles.editBtn, marginTop: 8 }} onClick={addVariant}>+ 옵션 추가</button>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>취소</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  createBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#fafafa', textAlign: 'left' },
  thCell: { padding: '10px 12px', fontWeight: 600, color: '#666', borderBottom: '1px solid #e8e8e8' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '10px 12px', verticalAlign: 'middle' },
  statusBadge: { color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10 },
  editBtn: { padding: '4px 10px', background: '#f5f5f5', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  statusBtn: { padding: '4px 10px', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  deleteBtn: { padding: '4px 10px', background: '#ffebee', color: '#e53935', border: '1px solid #ffcdd2', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 28, width: 640, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  formField: { display: 'flex', flexDirection: 'column', gap: 4 },
  formLabel: { fontSize: 12, color: '#666', fontWeight: 600 },
  formInput: { padding: '8px 12px', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  cancelBtn: { padding: '10px 20px', background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  saveBtn: { padding: '10px 24px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};

export default AdminGroupBuys;
